import api from "./api";

import {
  CreateTranslationMemorySegmentRegexData,
  TranslationMemory,
  TranslationMemorySegmentRegex,
} from "../translation/types";

const SEGMENT_REGEX_BASE_URL =
  "/api/_internal/translation-memory-segment-regex/";
const TRANSLATION_MEMORY_BASE_URL = "/api/_internal/translation-memory/";

/**
 * Get all translation memory segment regex patterns
 */
export const getTranslationMemorySegmentRegexList = async (): Promise<
  TranslationMemorySegmentRegex[]
> => {
  const response = await api.get<TranslationMemorySegmentRegex[]>(
    SEGMENT_REGEX_BASE_URL,
    {}
  );
  return response.data;
};

/**
 * Get a single translation memory segment regex pattern by ID
 */
export const getTranslationMemorySegmentRegex = async (
  id: number
): Promise<TranslationMemorySegmentRegex> => {
  const response = await api.get<TranslationMemorySegmentRegex>(
    SEGMENT_REGEX_BASE_URL + id,
    {}
  );
  return response.data;
};

/**
 * Create a new translation memory segment regex pattern
 */
export const createTranslationMemorySegmentRegex = async (
  data: CreateTranslationMemorySegmentRegexData
): Promise<TranslationMemorySegmentRegex> => {
  const response = await api.post(SEGMENT_REGEX_BASE_URL, data, {});
  return response.data;
};

/**
 * Update an existing translation memory segment regex pattern
 * Currently unused
export const updateTranslationMemorySegmentRegex = async (
  id: number,
  data: UpdateTranslationMemorySegmentRegexData
): Promise<TranslationMemorySegmentRegex> => {
  const response = await api.put(SEGMENT_REGEX_BASE_URL + id, data, {});
  return response.data;
};
*/

/**
 * Delete a translation memory segment regex pattern
 * Currently unused
export const deleteTranslationMemorySegmentRegex = async (
  id: number
): Promise<unknown> => {
  const response = await api.delete(SEGMENT_REGEX_BASE_URL + id, {});
  return response.data;
};
 */

/**
 * Search translation memory
 */
export const searchTranslationMemory = async (
  text: string,
  sourceLanguage: string | null,
  targetLanguage: string,
  byField: "source_text" | "target_text" = "source_text",
  skipIds: number[] = []
): Promise<TranslationMemory[]> => {
  if (
    (!sourceLanguage && byField === "source_text") ||
    !targetLanguage ||
    !text.trim()
  ) {
    return [];
  }
  const response = await api.post(
    TRANSLATION_MEMORY_BASE_URL + "fuzzy-search/",
    {
      text,
      source_language: sourceLanguage,
      target_language: targetLanguage,
      skip_ids: skipIds,
      by_field: byField,
    },
    {}
  );
  return response.data;
};

/**
 * Add a translation memory
 */
export const addTranslationMemory = async (
  sourceText: string,
  targetText: string,
  sourceLanguage: string,
  targetLanguage: string,
  customerId: number,
  translatorId: number,
  createdFromModel: string,
  createdFromModelId: number
): Promise<{ id: number }> => {
  const response = await api.post(
    TRANSLATION_MEMORY_BASE_URL + "add/",
    {
      source_text: sourceText,
      target_text: targetText,
      source_language: sourceLanguage,
      target_language: targetLanguage,
      customer_id: customerId,
      translator_id: translatorId,
      created_from_model: createdFromModel,
      created_from_model_id: createdFromModelId,
    },
    {}
  );
  return response.data;
};

export type TranslationMemoryUpdateEntry = {
  id: number;
  source_text: string;
  target_text: string;
};

export type TranslationMemoryNewEntry = {
  source_text: string;
  target_text: string;
};

export type UpdateTranslationMemoryEntriesRequest = {
  source_language: string;
  target_language: string;
  updated_entries: TranslationMemoryUpdateEntry[];
  new_entries: TranslationMemoryNewEntry[];
  deleted_entry_ids: number[];
  merge: boolean;
  use_vector_store?: boolean;
  uploaded_file?: File | null;
};

export type UpdateTranslationMemoryEntriesResponse = {
  updated: number;
  created: number;
  deleted: number;
  use_vector_store?: boolean;
};

export const updateTranslationMemoryEntries = async (
  data: UpdateTranslationMemoryEntriesRequest,
  token: string
): Promise<UpdateTranslationMemoryEntriesResponse> => {
  const formData = new FormData();
  formData.append("source_language", data.source_language);
  formData.append("target_language", data.target_language);
  formData.append("updated_entries", JSON.stringify(data.updated_entries));
  formData.append("new_entries", JSON.stringify(data.new_entries));
  formData.append("deleted_entry_ids", JSON.stringify(data.deleted_entry_ids));
  formData.append("merge", data.merge ? "1" : "0");
  if (data.use_vector_store !== undefined) {
    formData.append("use_vector_store", data.use_vector_store ? "1" : "0");
  }
  if (data.uploaded_file) {
    formData.append("uploaded_file", data.uploaded_file);
  }

  const response = await api.patch(
    TRANSLATION_MEMORY_BASE_URL + "update-entries/",
    formData,
    { headers: { token } }
  );
  return response.data;
};
